/**
 * Remembers the folder id the user opened an editor from, so the editor's
 * back-arrow can return there instead of the Workspace root.
 *
 * Why sessionStorage over URL state:
 *   Slate/Waveform editors trigger multiple client-side navigations during
 *   load + save (e.g. React Router state gets replaced on document reload
 *   redirects). navigate(-1) and location.state don't survive those. A
 *   sessionStorage entry keyed on the document id survives the entire
 *   editor session and clears when the tab closes.
 *
 * Fixes #2035: "When opening a transcript from within a folder (edit +
 * save) and move back with the arrow button (LEFT), you don't go back to
 * the folder, but to the Workspace."
 */

const KEY_PREFIX = 'scriptix.editorReturnFolder.';

/** Called from workspace.tsx when a document row is opened inside a folder. */
export const rememberReturnFolder = (
  documentId: string | number | undefined,
  folderId: string | number | null | undefined
): void => {
  if (documentId === undefined || documentId === null || documentId === '') {
    return;
  }
  const key = `${KEY_PREFIX}${documentId}`;
  try {
    if (folderId === null || folderId === undefined || folderId === '') {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, String(folderId));
    }
  } catch {
    /* private mode / storage full — silent fallback to workspace root */
  }
};

/** Called from editor back-buttons to decide where to navigate. */
export const getReturnFolder = (
  documentId: string | number | undefined
): string | null => {
  if (!documentId) return null;
  try {
    return sessionStorage.getItem(`${KEY_PREFIX}${documentId}`);
  } catch {
    return null;
  }
};
